import { SESSIONS_TEST_IDS } from '@/components/admin/sessions/contracts';
import { costLabel } from '@/lib/session-labels';
import type { StepBand } from '@matter_extractor/shared';

/**
 * The separator that opens a step, or one of its attempts (§12.3).
 *
 * ## One of the three things that survives the bare transcript
 *
 * « Les séparateurs d'étape, le tour malformé et l'incident » - and this one for
 * a reason of its own: it is the STRUCTURE of the conversation and not a detail.
 * A transcript of forty turns with no band is a transcript nobody can situate,
 * and situating is the first thing somebody debugging a protocol does.
 *
 * ## It is drawn on a REPLAY too, and that is what `stepBandAt` decides
 *
 * « Relancer l'étape » starts a new pass with its counters at zero (§8.4), so a
 * reader who saw no band would read the second attempt as a continuation of the
 * first - the same turns, counted twice. The band prints the attempt whenever
 * more than one is allowed, and the figures it carries are those of the PASS:
 * « 4 tours sur 6 » counts every turn of that pass, including the ones
 * further down the fil, which is what makes it readable before scrolling.
 *
 * ## A farewell step's band carries no cap (issue #163)
 *
 * « Sur une étape d'adieu, "étape d'adieu · 1 tour · coût", sans plafond »: the
 * step has no rules the engine reads (H76), so « 1 tour sur 2 » would print a
 * cap that decided nothing, and « sans plafond » would read as a refusal of
 * §7.1 that was never one. The attempt goes for the same reason.
 */

export interface StepBandRowProps {
  band: StepBand;
  /**
   * The step is a farewell step of the FROZEN protocol. A prop and not a field
   * of `StepBand` because the band is built in `shared`, and this row is the
   * only reader of the fact so far.
   */
  farewell: boolean;
}

export function StepBandRow({ band, farewell }: StepBandRowProps) {
  const replayed = band.attempt > 1;
  return (
    <li
      data-testid={SESSIONS_TEST_IDS.stepBand}
      data-step-id={band.stepId}
      data-attempt={band.attempt}
      data-farewell={farewell}
      className="flex flex-wrap items-center gap-x-3 gap-y-1 border-gray-200 border-y bg-bleu-nuit-tint px-5 py-2 font-semibold text-bleu-nuit text-caption uppercase tracking-wide"
    >
      <span>
        Étape {band.index} · {band.name}
        {farewell ? (
          <span className="font-normal normal-case tracking-normal"> · étape d’adieu</span>
        ) : null}
      </span>
      <span className="ml-auto font-normal text-bleu-nuit-deep normal-case tracking-normal">
        {band.turnCount} tour{band.turnCount > 1 ? 's' : ''}
        {farewell ? null : band.maxTurns === null ? ' · sans plafond' : ` sur ${band.maxTurns}`}
        {!farewell && (band.attemptsAllowed > 1 || replayed) ? (
          <span className={replayed ? 'font-semibold text-jaune-deep' : ''}>
            {' · '}essai {band.attempt} sur {band.attemptsAllowed}
          </span>
        ) : null}
        {' · '}
        {costLabel(band.costEur)}
      </span>
    </li>
  );
}
